import React from 'react'
import { Card, Row, Col, Container } from 'react-bootstrap'
import { Zap, BarChart3, MapPin, Gauge, Repeat2, Split, SquareStack } from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { useNavigate } from 'react-router-dom'

const ViewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
`

const ScrollableContent = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  min-height: 0;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
  padding: 1.5rem 0;
  background: linear-gradient(180deg, rgba(128, 0, 133, 0.1) 0%, rgba(128, 0, 133, 0) 100%);
  border-radius: 12px;
  border: 1px solid rgba(128, 0, 133, 0.2);

  h1 {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    background: linear-gradient(90deg, #fff 0%, #800085 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 800px;
    margin: 0 auto;
  }
`

const StatsCard = styled(Card)`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  height: 100%;
  padding: 1.25rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  .stat-value {
    font-size: 2.5rem;
    font-weight: 600;
    margin: 0;
    background: linear-gradient(90deg, #fff 0%, #800085 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .stat-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
    margin: 0;
  }
`

const IconWrapper = styled.div<{ $color?: string }>`
  background: ${(props) => props.$color || 'rgba(128, 0, 133, 0.2)'};
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: transform 0.2s ease;

  svg {
    width: 32px;
    height: 32px;
    color: #800085;
  }
`

const FeatureCard = styled(Card)`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);

    ${IconWrapper} {
      transform: scale(1.1);
    }
  }
`

const CardHeader = styled(Card.Header)`
  background: rgba(128, 0, 133, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.5rem;
  font-weight: 500;
`

const CardBody = styled(Card.Body)`
  padding: 1.5rem;
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
`

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: rgba(255, 255, 255, 0.7);

    &:before {
      content: '•';
      color: #800085;
      font-weight: bold;
    }
  }
`

const InferenceOverviewSubView: React.FC = () => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const navigate = useNavigate()
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Calculate statistics
  const stats = {
    totalModels: modelsData?.models?.length || 0,
    deployedModels:
      modelsData?.models?.filter((m) => m.deployment?.status.toLowerCase() === 'in_service')
        .length || 0,
    inferenceReady:
      modelsData?.models?.filter(
        (m) =>
          m.state.toLowerCase() === 'trained' && m.deployment?.status.toLowerCase() === 'in_service'
      ).length || 0,
  }

  const handleCardClick = (path: string) => {
    if (selectedProjectId) {
      navigate(`/inference/${selectedProjectId}/${path}`)
    }
  }

  return (
    <ViewContainer>
      <ScrollableContent>
        {/* Page Header */}
        <PageHeader>
          <h1>Inference Tools</h1>
          <p>
            Leverage the power of your trained models with our comprehensive suite of inference
            tools. From active learning to real-time predictions, explore the full potential of your
            AI models.
          </p>
        </PageHeader>

        {/* Statistics Section */}
        <Container fluid className="mb-3">
          <Row className="g-3">
            <Col lg={4}>
              <StatsCard>
                <IconWrapper $color="rgba(85, 60, 154, 0.2)">
                  <Gauge size={32} />
                </IconWrapper>
                <p className="stat-value">{stats.totalModels}</p>
                <p className="stat-label">Total Models</p>
              </StatsCard>
            </Col>
            <Col lg={4}>
              <StatsCard>
                <IconWrapper $color="rgba(39, 103, 73, 0.2)">
                  <Zap size={32} />
                </IconWrapper>
                <p className="stat-value">{stats.deployedModels}</p>
                <p className="stat-label">Deployed Models</p>
              </StatsCard>
            </Col>
            <Col lg={4}>
              <StatsCard>
                <IconWrapper $color="rgba(40, 94, 97, 0.2)">
                  <BarChart3 size={32} />
                </IconWrapper>
                <p className="stat-value">{stats.inferenceReady}</p>
                <p className="stat-label">Inference Ready</p>
              </StatsCard>
            </Col>
          </Row>
        </Container>

        {/* Features Section */}
        <Container fluid>
          <Row className="g-4">
            <Col xs={12} md={6} lg={4}>
              <FeatureCard onClick={() => handleCardClick('adaptive-active-labeling')}>
                <CardHeader>
                  <IconWrapper>
                    <Repeat2 size={32} />
                  </IconWrapper>
                  Adaptive Active Labeling
                </CardHeader>
                <CardBody>
                  <p>
                    Continuously improve your model's performance through intelligent data selection
                    and iterative learning.
                  </p>
                  <FeatureList>
                    <li>Identify most informative samples for labeling</li>
                    <li>Optimize model training through active learning</li>
                    <li>Streamline the annotation process</li>
                    <li>Track model improvement over time</li>
                  </FeatureList>
                </CardBody>
              </FeatureCard>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <FeatureCard onClick={() => handleCardClick('realtime')}>
                <CardHeader>
                  <IconWrapper>
                    <BarChart3 size={32} />
                  </IconWrapper>
                  Real-Time Inference
                </CardHeader>
                <CardBody>
                  <p>
                    Perform instant predictions with comprehensive visualization and analysis tools.
                  </p>
                  <FeatureList>
                    <li>Instant model predictions on new data</li>
                    <li>Detailed confidence scores and statistics</li>
                    <li>Visual result analysis</li>
                    <li>Batch processing capabilities</li>
                  </FeatureList>
                </CardBody>
              </FeatureCard>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <FeatureCard onClick={() => handleCardClick('routing')}>
                <CardHeader>
                  <IconWrapper>
                    <Split size={32} />
                  </IconWrapper>
                  Adaptive Inference Routing
                </CardHeader>
                <CardBody>
                  <p>
                    Intelligently route incoming images to the best-matched deployed model using
                    embedding-based similarity analysis.
                  </p>
                  <FeatureList>
                    <li>Single endpoint for multiple deployed models</li>
                    <li>Embedding-based model selection</li>
                    <li>Automatic routing to optimal model</li>
                    <li>Training data similarity matching</li>
                  </FeatureList>
                </CardBody>
              </FeatureCard>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <FeatureCard onClick={() => handleCardClick('change-detection')}>
                <CardHeader>
                  <IconWrapper>
                    <SquareStack size={32} />
                  </IconWrapper>
                  Change Detection
                </CardHeader>
                <CardBody>
                  <p>
                    Detect and analyze changes between images over time using advanced computer
                    vision algorithms.
                  </p>
                  <FeatureList>
                    <li>Temporal image comparison analysis</li>
                    <li>Automated change highlighting</li>
                    <li>Before/after visualization tools</li>
                    <li>Customizable sensitivity thresholds</li>
                  </FeatureList>
                </CardBody>
              </FeatureCard>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <FeatureCard onClick={() => handleCardClick('map')}>
                <CardHeader>
                  <IconWrapper>
                    <MapPin size={32} />
                  </IconWrapper>
                  Map Inference
                </CardHeader>
                <CardBody>
                  <p>Beta: Visualize and analyze geospatial predictions on interactive maps.</p>
                  <FeatureList>
                    <li>Geographic visualization of predictions</li>
                    <li>Interactive map interface</li>
                    <li>Spatial analysis tools</li>
                    <li>Location-based insights</li>
                  </FeatureList>
                </CardBody>
              </FeatureCard>
            </Col>
          </Row>
        </Container>
      </ScrollableContent>
    </ViewContainer>
  )
}

export default InferenceOverviewSubView
